import React, { useState, useEffect } from 'react';
import { useRenderCount, useGlobalContext, currentTripStateIdVar, useCurrentRooms, useShortlist, FeatureFlagEnum, hasFeatureFlag, parseBoolean, TripType, useSearch, } from '@honeycomb/data';
import { Button, Clone, Stacker, useDisclosure } from '@honeycomb/ui-core';
import { useStorageState } from 'react-storage-hooks';
import { useRouter } from 'next/router';
import { getQueryString } from '../../../utils/getQueryString';
import { HotelCard } from '../HotelCard/HotelCard';
import { HotelSearchNoResult } from '../HotelSearchNoResult/HotelSearchNoResult';
import { FlightDetailsDialog } from '../../flight/FlightDetails/FlightDetailsDialog';
import { validateFlightDetails } from '../../flight/utils/utils';
import { HotelReviewsDialog } from '../../hotel/HotelReviewsDialog/HotelReviewsDialog';
import { getStorageObject } from '../../../utils/getStorageObject';
import { csvContainsAny, useTripTypeFromTrip } from '../../../utils';
import { getOccupancySummary } from '../../../utils/getOccupancySummary';
export function HotelSearchResults({ hotelSearchResults, onFetchNextHotels, onResetFilters, onOpenSearchUnit, hotelSearchResultsSlot = () => null, }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    // State for flight details dialog
    const [_, setCurrentRooms] = useCurrentRooms();
    const { resourceStrings: { HOTELS_LOADMORE, ADULT, ADULTS, CHILD, CHILDREN, NIGHT, NIGHTS }, configSettings: { EnableShortlist, ProductCacheEnabled, TabNavigationDestinations, HolidayTabNavigationEnabled, HotelTabNavigationEnabled, EnableOccupancySummary, }, } = useGlobalContext();
    const shortlistEnabled = parseBoolean(EnableShortlist) || hasFeatureFlag(FeatureFlagEnum.SHORTLIST);
    const getShortlistResults = useShortlist([], !shortlistEnabled);
    const estabsInShortlist = (_d = (_c = (_b = (_a = getShortlistResults === null || getShortlistResults === void 0 ? void 0 : getShortlistResults.data) === null || _a === void 0 ? void 0 : _a.getShortlist) === null || _b === void 0 ? void 0 : _b.hotels) === null || _c === void 0 ? void 0 : _c.map((hotel) => hotel.estabId.toString())) !== null && _d !== void 0 ? _d : [];
    const { loading: hotelsLoading, called: hotelsCalled, error, data } = hotelSearchResults;
    // State for flight details dialog
    const [flightDetailsDialogOpen, { open: handleOpenFlightDetailsDialog, close: handleCloseFlightDetailsDialog }] = useDisclosure(false);
    const [flightDetailsDialogExited, setFlightDetailsDialogExited] = React.useState(true);
    const [flightDetails, setFlightDetails] = useState();
    // State for reviews dialog
    const [reviewsDialogOpen, { open: handleOpenReviewsDialog, close: handleCloseReviewsDialog }] = useDisclosure(false);
    const [reviewsDialogExited, setReviewsDialogExited] = React.useState(true);
    const [reviewDetails, setReviewDetails] = useState();
    const [hotelSearch] = useSearch([]);
    const storageObject = getStorageObject();
    const [connectingFlight, _setConnectingFlight] = useStorageState(storageObject, 'connectingFlight');
    if (connectingFlight && (connectingFlight === null || connectingFlight === void 0 ? void 0 : connectingFlight.tripId) !== currentTripStateIdVar()) {
        storageObject.removeItem('connectingFlight'); // Should this not be a useEffect? Calling this during the render looks wrong.
    }
    const loading = hotelsLoading || !hotelsCalled;
    const hotels = (_e = data === null || data === void 0 ? void 0 : data.hotelSearchResults) === null || _e === void 0 ? void 0 : _e.hotels;
    const totalHotelCount = (_g = (_f = data === null || data === void 0 ? void 0 : data.hotelSearchResults) === null || _f === void 0 ? void 0 : _f.hotelCount) !== null && _g !== void 0 ? _g : 0; // The total number of hotels in the result set.
    const filteredHotelCount = (_j = (_h = data === null || data === void 0 ? void 0 : data.hotelSearchResults) === null || _h === void 0 ? void 0 : _h.filteredHotelCount) !== null && _j !== void 0 ? _j : 0; // The number of hotels that match the current filter settings.
    const visibleHotelCount = (_k = hotels === null || hotels === void 0 ? void 0 : hotels.length) !== null && _k !== void 0 ? _k : 0; // The number of hotels that we are currently displaying.
    const router = useRouter();
    const query = router === null || router === void 0 ? void 0 : router.query;
    const queryString = getQueryString(query);
    const isCacheEnabled = parseBoolean(ProductCacheEnabled);
    const hasMatchingTabDestinations = React.useMemo(() => {
        var _a, _b, _c;
        const destinationIds = isCacheEnabled
            ? (_b = (_a = hotelSearch.destinations) === null || _a === void 0 ? void 0 : _a.flatMap((dest) => { var _a; return (_a = dest === null || dest === void 0 ? void 0 : dest.id) !== null && _a !== void 0 ? _a : []; })) !== null && _b !== void 0 ? _b : []
            : (_c = hotelSearch.destinationIds) !== null && _c !== void 0 ? _c : [];
        return csvContainsAny(TabNavigationDestinations, destinationIds);
    }, [TabNavigationDestinations, hotelSearch.destinationIds, hotelSearch.destinations, isCacheEnabled]);
    // !! DEV-Only !! Counts and prints number of renders for component
    useRenderCount('hotelsList');
    useEffect(() => {
        var _a;
        const handleRouteChange = () => {
            const savedScrollId = sessionStorage.getItem('srpCardClickedId');
            if (savedScrollId) {
                const el = document.getElementById(savedScrollId);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
                sessionStorage.removeItem('srpCardClickedId');
            }
        };
        (_a = router.events) === null || _a === void 0 ? void 0 : _a.on('routeChangeComplete', handleRouteChange);
        return () => { var _a; return (_a = router.events) === null || _a === void 0 ? void 0 : _a.off('routeChangeComplete', handleRouteChange); };
    }, [router]);
    useEffect(() => {
        setCurrentRooms({
            rooms: [],
            selectedRoomIndex: 0,
            showPerPersonPrices: false,
            soldOut: false,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleReviewsDialogTriggerClick = React.useCallback((hotel) => (origin) => {
        setReviewDetails({
            hotel,
            origin,
        });
        handleOpenReviewsDialog();
        setReviewsDialogExited(false);
    }, [handleOpenReviewsDialog]);
    const tripType = useTripTypeFromTrip();
    const numberOfNights = (_m = (_l = hotels === null || hotels === void 0 ? void 0 : hotels[0]) === null || _l === void 0 ? void 0 : _l.numberOfNights) !== null && _m !== void 0 ? _m : 0;
    const enableOccupancySummary = parseBoolean(EnableOccupancySummary);
    const occupancySummary = React.useMemo(() => {
        var _a;
        if (!enableOccupancySummary) {
            return undefined;
        }
        return getOccupancySummary((_a = hotelSearch.rooms) !== null && _a !== void 0 ? _a : [], {
            ADULT,
            ADULTS,
            CHILD,
            CHILDREN,
            NIGHT,
            NIGHTS,
        }, '', true, numberOfNights, true);
    }, [enableOccupancySummary, hotelSearch.rooms, ADULT, ADULTS, CHILD, CHILDREN, NIGHT, NIGHTS, numberOfNights]);
    if (error) {
        return React.createElement("div", null,
            "Error!: ",
            error.message);
    }
    if (!loading && data && !(hotels === null || hotels === void 0 ? void 0 : hotels.length)) {
        return (React.createElement(HotelSearchNoResult, { areFiltersApplied: filteredHotelCount < totalHotelCount, onResetFilters: onResetFilters, onOpenSearchUnit: onOpenSearchUnit, isHolidaySearch: tripType === TripType.Holiday }));
    }
    const tabFlag = tripType === TripType.Holiday ? HolidayTabNavigationEnabled : HotelTabNavigationEnabled;
    const isOpenInNewTab = hasMatchingTabDestinations || parseBoolean(tabFlag);
    return (React.createElement(React.Fragment, null,
        React.createElement(Stacker, { spacing: { xs: 5, xl: 6 }, alignItems: "center" }, hotels === null || hotels === void 0 ? void 0 :
            hotels.map((searchResult, index, arr) => {
                const { estabId } = searchResult.hotel;
                return (React.createElement(React.Fragment, { key: `hotel-result-${estabId}` },
                    React.createElement(hotelSearchResultsSlot, { index, hotels: arr }),
                    React.createElement(HotelCard, { searchResult: searchResult, queryString: queryString, onFlightDetailsClick: validateFlightDetails(searchResult.flight)
                            ? () => {
                                var _a, _b;
                                setFlightDetails({
                                    outboundLeg: (_a = searchResult.flight) === null || _a === void 0 ? void 0 : _a.outboundLeg,
                                    inboundLeg: (_b = searchResult.flight) === null || _b === void 0 ? void 0 : _b.inboundLeg,
                                });
                                handleOpenFlightDetailsDialog();
                                setFlightDetailsDialogExited(false);
                            }
                            : undefined, isInShortlist: estabsInShortlist === null || estabsInShortlist === void 0 ? void 0 : estabsInShortlist.includes(estabId), onReviewsDialogTriggerClick: handleReviewsDialogTriggerClick(searchResult.hotel), "data-id": "hotel_search_results_listing", key: `hotel-card-${estabId}`, isOpenInNewTab: isOpenInNewTab, occupancySummary: occupancySummary })));
            }),
            loading && React.createElement(Clone, { copies: 6, renderChildren: () => React.createElement(HotelCard, { "data-id": "skeleton-card" }) }),
            !loading && visibleHotelCount < filteredHotelCount && (React.createElement(Button, { onClick: onFetchNextHotels, fullWidth: true, variant: "outlined", size: "l", color: "primary", "data-id": "show_more_button" }, HOTELS_LOADMORE))),
        (flightDetailsDialogOpen || !flightDetailsDialogExited) && (React.createElement(FlightDetailsDialog, { outboundLeg: (flightDetails === null || flightDetails === void 0 ? void 0 : flightDetails.outboundLeg) || undefined, inboundLeg: (flightDetails === null || flightDetails === void 0 ? void 0 : flightDetails.inboundLeg) || undefined, open: flightDetailsDialogOpen, onClose: handleCloseFlightDetailsDialog, onExited: () => setFlightDetailsDialogExited(true) })),
        reviewDetails && (reviewsDialogOpen || !reviewsDialogExited) && (React.createElement(HotelReviewsDialog, { hotel: reviewDetails.hotel, origin: reviewDetails.origin, open: reviewsDialogOpen, onClose: handleCloseReviewsDialog, onExited: () => setReviewsDialogExited(true) }))));
}
